/**
 * Runtime shape validators for the overview-dashboard responses.
 *
 * Each validator is a pure function: ``unknown -> T``. On the first
 * shape mismatch it throws an ``Error`` with a path-style message
 * (``rows[2].content_quality: expected number|null, got string``) so
 * the caller can surface the failure without guessing where the
 * contract drifted.
 *
 * Why this exists alongside the TypeScript types:
 *   ``OverviewSummaryResponse`` etc. are compile-time only -- nothing
 *   stops the backend from returning a renamed field that crashes
 *   the chart renderer at runtime. These validators are the
 *   defence-in-depth layer that fails fast at the API boundary
 *   instead of inside a deeply nested SVG path computation.
 */

import type {
  IngestedContentSlice,
  OverviewContentDistributionResponse,
  KpiCard,
  KpiUnit,
  OverviewDomainRow,
  OverviewDomainsResponse,
  OverviewSummaryResponse,
  OverviewUsageSeries,
  OverviewUsageTrendsResponse,
  UsageBucket,
  UsageTrendInterval,
} from '../../types/overview';

// ---------------------------------------------------------------------------
// Primitive guards
// ---------------------------------------------------------------------------

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function need<T>(v: T | undefined, path: string, what: string): T {
  if (v === undefined) throw new Error(`${path}: missing ${what}`);
  return v;
}

function asString(v: unknown, path: string): string {
  if (typeof v !== 'string') throw new Error(`${path}: expected string, got ${typeof v}`);
  return v;
}

function asNumber(v: unknown, path: string): number {
  if (typeof v !== 'number' || Number.isNaN(v)) {
    throw new Error(`${path}: expected number, got ${typeof v}`);
  }
  return v;
}

function asInt(v: unknown, path: string): number {
  const n = asNumber(v, path);
  if (!Number.isFinite(n) || Math.floor(n) !== n) {
    throw new Error(`${path}: expected integer, got ${n}`);
  }
  return n;
}

function asNumberOrNull(v: unknown, path: string): number | null {
  if (v === null) return null;
  return asNumber(v, path);
}

function asStringOrNull(v: unknown, path: string): string | null {
  if (v === null) return null;
  return asString(v, path);
}

const USAGE_TREND_INTERVALS: ReadonlySet<UsageTrendInterval> = new Set<UsageTrendInterval>([
  'day',
  'week',
  'month',
]);
function asUsageTrendInterval(v: unknown, path: string): UsageTrendInterval {
  const s = asString(v, path);
  if (!USAGE_TREND_INTERVALS.has(s as UsageTrendInterval)) {
    throw new Error(`${path}: unknown interval '${s}'`);
  }
  return s as UsageTrendInterval;
}

const KPI_UNITS: ReadonlySet<KpiUnit> = new Set<KpiUnit>(['count', 'percent', 'score', 'ms']);
function asKpiUnitOrNull(v: unknown, path: string): KpiUnit | null {
  if (v === null) return null;
  const s = asString(v, path);
  if (!KPI_UNITS.has(s as KpiUnit)) {
    // Unknown unit is non-fatal: degrade to ``null`` so the UI picks
    // a generic formatter rather than rejecting the whole response.
    return null;
  }
  return s as KpiUnit;
}

function asArray(v: unknown, path: string): unknown[] {
  if (!Array.isArray(v)) throw new Error(`${path}: expected array, got ${typeof v}`);
  return v;
}

function asObject(v: unknown, path: string): Record<string, unknown> {
  if (!isPlainObject(v)) throw new Error(`${path}: expected object`);
  return v;
}

// ---------------------------------------------------------------------------
// Compound validators
// ---------------------------------------------------------------------------

function validateKpiCard(v: unknown, path: string): KpiCard {
  const o = asObject(v, path);
  return {
    key: asString(need(o.key, path, 'key'), `${path}.key`),
    label: asString(need(o.label, path, 'label'), `${path}.label`),
    value: asNumber(need(o.value, path, 'value'), `${path}.value`),
    previous_value: asNumberOrNull(o.previous_value ?? null, `${path}.previous_value`),
    delta_pct: asNumberOrNull(o.delta_pct ?? null, `${path}.delta_pct`),
    unit: asKpiUnitOrNull(o.unit ?? null, `${path}.unit`),
  };
}

function validateUsageBucket(v: unknown, path: string): UsageBucket {
  const o = asObject(v, path);
  return {
    bucket: asString(need(o.bucket, path, 'bucket'), `${path}.bucket`),
    count: asInt(need(o.count, path, 'count'), `${path}.count`),
  };
}

function validateOverviewUsageSeries(v: unknown, path: string): OverviewUsageSeries {
  const o = asObject(v, path);
  return {
    channel: asString(need(o.channel, path, 'channel'), `${path}.channel`),
    buckets: asArray(o.buckets ?? [], `${path}.buckets`).map((b, i) =>
      validateUsageBucket(b, `${path}.buckets[${i}]`),
    ),
    total: asInt(need(o.total, path, 'total'), `${path}.total`),
  };
}

function validateIngestedContentSlice(v: unknown, path: string): IngestedContentSlice {
  const o = asObject(v, path);
  return {
    site_id: asString(need(o.site_id, path, 'site_id'), `${path}.site_id`),
    site_name: asString(need(o.site_name, path, 'site_name'), `${path}.site_name`),
    count: asInt(need(o.count, path, 'count'), `${path}.count`),
  };
}

function validateDomainRow(v: unknown, path: string): OverviewDomainRow {
  const o = asObject(v, path);
  return {
    site_id: asString(need(o.site_id, path, 'site_id'), `${path}.site_id`),
    site_name: asString(need(o.site_name, path, 'site_name'), `${path}.site_name`),
    content_quality: asNumberOrNull(o.content_quality ?? null, `${path}.content_quality`),
    ingested_count: asInt(need(o.ingested_count, path, 'ingested_count'), `${path}.ingested_count`),
    experiments: asInt(need(o.experiments, path, 'experiments'), `${path}.experiments`),
    deployed_assistants: asInt(
      need(o.deployed_assistants, path, 'deployed_assistants'),
      `${path}.deployed_assistants`,
    ),
    avg_user_rating: asNumberOrNull(
      o.avg_user_rating ?? null,
      `${path}.avg_user_rating`,
    ),
    last_used_at: asStringOrNull(o.last_used_at ?? null, `${path}.last_used_at`),
    unanswered_count: asInt(
      need(o.unanswered_count, path, 'unanswered_count'),
      `${path}.unanswered_count`,
    ),
    avg_llm_rating: asNumberOrNull(o.avg_llm_rating ?? null, `${path}.avg_llm_rating`),
    llm_eval_count: asInt(o.llm_eval_count ?? 0, `${path}.llm_eval_count`),
    liked_count: asInt(o.liked_count ?? 0, `${path}.liked_count`),
    disliked_count: asInt(o.disliked_count ?? 0, `${path}.disliked_count`),
    total_queries: asInt(o.total_queries ?? 0, `${path}.total_queries`),
  };
}

// ---------------------------------------------------------------------------
// Public top-level validators
// ---------------------------------------------------------------------------

export function validateOverviewSummaryResponse(raw: unknown): OverviewSummaryResponse {
  const o = asObject(raw, '$');
  return {
    org_id: asString(need(o.org_id, '$', 'org_id'), '$.org_id'),
    cards: asArray(o.cards ?? [], '$.cards').map((c, i) =>
      validateKpiCard(c, `$.cards[${i}]`),
    ),
    aggregate_updated_at: asString(
      need(o.aggregate_updated_at, '$', 'aggregate_updated_at'),
      '$.aggregate_updated_at',
    ),
  };
}

export function validateOverviewUsageTrendsResponse(
  raw: unknown,
): OverviewUsageTrendsResponse {
  const o = asObject(raw, '$');
  return {
    org_id: asString(need(o.org_id, '$', 'org_id'), '$.org_id'),
    start_date: asString(need(o.start_date, '$', 'start_date'), '$.start_date'),
    end_date: asString(need(o.end_date, '$', 'end_date'), '$.end_date'),
    interval: asUsageTrendInterval(
      need(o.interval, '$', 'interval'),
      '$.interval',
    ),
    series: asArray(o.series ?? [], '$.series').map((s, i) =>
      validateOverviewUsageSeries(s, `$.series[${i}]`),
    ),
    total_queries: asInt(
      need(o.total_queries, '$', 'total_queries'),
      '$.total_queries',
    ),
    aggregate_updated_at: asString(
      need(o.aggregate_updated_at, '$', 'aggregate_updated_at'),
      '$.aggregate_updated_at',
    ),
  };
}

export function validateOverviewDomainsResponse(raw: unknown): OverviewDomainsResponse {
  const o = asObject(raw, '$');
  return {
    org_id: asString(need(o.org_id, '$', 'org_id'), '$.org_id'),
    rows: asArray(o.rows ?? [], '$.rows').map((r, i) =>
      validateDomainRow(r, `$.rows[${i}]`),
    ),
    page: asInt(need(o.page, '$', 'page'), '$.page'),
    page_size: asInt(need(o.page_size, '$', 'page_size'), '$.page_size'),
    total_count: asInt(need(o.total_count, '$', 'total_count'), '$.total_count'),
    aggregate_updated_at: asString(
      need(o.aggregate_updated_at, '$', 'aggregate_updated_at'),
      '$.aggregate_updated_at',
    ),
  };
}

export function validateOverviewContentDistributionResponse(
  raw: unknown,
): OverviewContentDistributionResponse {
  const o = asObject(raw, '$');
  return {
    org_id: asString(need(o.org_id, '$', 'org_id'), '$.org_id'),
    content_distribution: asArray(
      o.content_distribution ?? [],
      '$.content_distribution',
    ).map((s, i) => validateIngestedContentSlice(s, `$.content_distribution[${i}]`)),
    aggregate_updated_at: asString(
      need(o.aggregate_updated_at, '$', 'aggregate_updated_at'),
      '$.aggregate_updated_at',
    ),
  };
}
